import { Check } from 'lucide-react'

import { formatUsdc, getEarnProgress } from '~/app/settings/team/upgrade/utils'
import { Skeleton, Tooltip } from '~/components/component-library'
import { cn } from '~/utils/tailwind-utils'

// Smallest fill that still reads as progress when the balance is tiny.
const MIN_VISIBLE_PERCENT = 2

// How much of the plan's Earn minimum the org already holds. The bar is
// keyboard-focusable so the remainder tooltip isn't hover-only, and the caption
// keeps the numbers visible on touch.
export const PlanEarnProgress = ({
  earnMinimum,
  amountNeeded,
  planLabel,
  isLoading,
}: {
  earnMinimum: number
  amountNeeded: number | undefined
  planLabel: string
  isLoading: boolean
}) => {
  if (isLoading || amountNeeded === undefined)
    return (
      <div className="space-y-2">
        <Skeleton height={0.5} />
        <Skeleton length={6} height={0.75} />
      </div>
    )

  const { held, remaining, percent } = getEarnProgress({
    earnMinimum,
    amountNeeded,
  })
  const fillPercent =
    percent > 0 ? Math.max(percent, MIN_VISIBLE_PERCENT) : percent
  const isComplete = percent >= 100
  const caption = `${formatUsdc(held)} / ${formatUsdc(earnMinimum)} USDC`
  const unlockedLabel = `${planLabel} unlocked`
  const tooltip = isComplete
    ? `You’ve unlocked ${planLabel}`
    : `Just ${formatUsdc(remaining)} USDC left to unlock ${planLabel}`

  return (
    <div className="space-y-1.5">
      {/* The padding enlarges the hover target without growing the thin bar. */}
      <Tooltip
        triggerClassName="relative -my-2 flex w-full py-2"
        // Below the bar, over the caption it repeats, not the copy above.
        position="bottom"
        content={tooltip}
      >
        <div
          role="progressbar"
          tabIndex={0}
          aria-label={`Earn balance toward ${planLabel}`}
          aria-valuemin={0}
          aria-valuemax={earnMinimum}
          aria-valuenow={held}
          aria-valuetext={isComplete ? unlockedLabel : caption}
          className={cn(
            'h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-black',
          )}
        >
          <div
            className={cn(
              'h-full rounded-full transition-[width,background-color] duration-300 ease-out',
              { 'bg-blue-500': !isComplete, 'bg-green-500': isComplete },
            )}
            style={{ width: `${fillPercent}%` }}
          />
        </div>
      </Tooltip>
      {/* An unlocked plan swaps the amounts for the label, so a balance above
          the minimum never reads as a cost. */}
      {isComplete ? (
        <p className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
          <Check className="size-3.5" strokeWidth={3} />
          {unlockedLabel}
        </p>
      ) : (
        <p className="text-xs text-secondary">{caption}</p>
      )}
    </div>
  )
}
